import { useNavigate } from '@tanstack/react-router'
import { Language } from '../services/adminService'
import { formatDate } from '../utils/dateUtils'
import { Table, TableColumn } from './Table'
import { Switch } from './ui/Switch'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Button } from './ui/Button'

interface LanguageListProps {
  languages: Language[]
  onToggleStatus: (language: Language) => void
  onEdit: (language: Language) => void
  togglingLanguageId?: string | null
}

export function LanguageList({ 
  languages, 
  onToggleStatus,
  onEdit, 
  togglingLanguageId 
}: LanguageListProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const getStatusBadge = (isActive: boolean) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
    }
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${isActive ? styles.active : styles.inactive}`}>
{isActive ? t('languages.status.active') : t('languages.status.inactive')}
      </span>
    )
  }

  // Status options for filter
  const statusOptions = [
    { value: 'true', label: t('languages.status.active') },
    { value: 'false', label: t('languages.status.inactive') }
  ]

  const columns: TableColumn<Language>[] = [
    {
      key: 'status',
      header: t('languages.fields.status'),
      sortKey: 'isActive',
      sortable: true,
      className: 'w-40',
      filterable: {
        type: 'select',
        options: statusOptions,
        filterKey: 'isActive'
      },
      render: (language: Language) => (
        <Switch
          key={`switch-${language.id}`}
          checked={language.isActive}
          onChange={() => onToggleStatus(language)}
          disabled={togglingLanguageId === language.id}
          size="sm"
        />
      )
    },
    {
      key: 'language',
      header: t('languages.fields.language'),
      sortKey: 'name',
      sortable: true,
      filterable: {
        type: 'text',
        placeholder: t('filters.filterBy.nameOrCode'),
        filterKey: 'name'
      },
      render: (language: Language) => (
        <div className="flex items-center">
          <span className="text-lg mr-3">{language.flag}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-900">
                {language.name}
              </div>
              <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono uppercase text-gray-600">
                {language.code}
              </span>
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              {language.nativeName}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'order',
      header: t('languages.fields.order'),
      sortKey: 'order',
      sortable: true,
      className: 'w-20',
      filterable: {
        type: 'text',
        placeholder: t('filters.filterBy.order'),
        filterKey: 'order'
      },
      render: (language: Language) => (
        <span className="text-sm text-gray-900">
          {language.order}
        </span>
      )
    },
    {
      key: 'created',
      header: t('languages.fields.created'),
      sortKey: 'createdAt',
      sortable: true,
      filterable: {
        type: 'date',
        filterKey: 'createdAt'
      },
      render: (language: Language) => (
        <span className="text-sm text-gray-500">
          {formatDate(language.createdAt, undefined, t)}
        </span>
      )
    },
    {
      key: 'actions',
      header: t('languages.fields.actions'),
      className: 'text-right w-32',
      filterable: {
        type: 'none'
      },
      render: (language: Language) => (
        <div className="flex">
          <Button
            onClick={() => onEdit(language)}
            variant="primary"
            size="sm"
          >
            {t('languages.actions.edit')}
          </Button>
        </div>
      )
    }
  ]

  return (
    <div>
              <Table
        data={languages}
        columns={columns}
        searchableFields={['name', 'nativeName', 'code']}
        searchPlaceholder={t('languages.searchPlaceholder')}
        emptyMessage="No languages found"
        emptySubMessage="Try adjusting your search or add your first language"
        pageSize={10}
        t={t}
      />
    </div>
  )
} 